import { Formula } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { getDisplayValueForScore } from "src/utils/scores";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { DimensionValue } from "src/api/client-api/olapScores/olapScoresSchema.ts";
import { ValidationMessagesByKey } from "./validationTypes.ts";
import { HTMLBox } from "../../HTMLBox/HTMLBox.tsx";
import { WhiteBox, FormUnit, Banner } from "@foodpilot/foods";
import { Stack, Typography, useTheme } from "@mui/material";
import { usePropertyContext } from "./context/usePropertyContext.tsx";
import { CommentDialog } from "./CommentDialog/CommentDialog.tsx";
import { FileUploadAttachmentDialog } from "./FileUploadDialog/FileUploadAttachmentDialog.tsx";
import { PropertyActionsPopover } from "./PropertyActions/PropertyActionsPopover.tsx";
import { FormPropertyComponent } from "./FormProperty.tsx";
import { overrideProperty } from "./context/utils/overrideProperty.ts";
import { useQuestionnaireFormContext } from "../context/useQuestionnaireForm.tsx";

export type FormPropertyContainerProps = {
    dimensionValue?: DimensionValue;
    gradeDimensionValue?: DimensionValue;
    validationErrors?: ValidationMessagesByKey;
};
export const FormPropertyTopLevel = (props: FormPropertyContainerProps) => {
    const { dimensionValue, gradeDimensionValue } = props;
    const theme = useTheme();
    const { ssqSsqHeadingSsqProperty, scoreUnit } = usePropertyContext();
    const { ingredientGenericValues } = useQuestionnaireFormContext();

    const property = overrideProperty({
        originalProperty: ssqSsqHeadingSsqProperty.ssqProperty,
        potentialOverrides: ssqSsqHeadingSsqProperty,
        ingredientGenericValue: ingredientGenericValues[ssqSsqHeadingSsqProperty.ssqProperty.id],
    });

    const { lastSelectedVariation, selectedScorePrecision } = useEntityScores();

    const propertyScoreValue = OlapScoresService.normalizeNumericalScore(dimensionValue?.score.value);
    const propertyGradeValue = OlapScoresService.normalizeNumericalScore(gradeDimensionValue?.score.value);

    // TODO refactor when proper calculation type is implemented and PEF is handled differently
    const eligibleFormulas = property.formulas.filter(
        (formula: Formula) =>
            formula.score === undefined ||
            formula.score.id === lastSelectedVariation.scoreId ||
            lastSelectedVariation.type === "pef",
    );
    const doDisplayIndicator =
        (eligibleFormulas.length > 0 || property.children.length > 0) && propertyScoreValue !== null;

    let hint = ssqSsqHeadingSsqProperty.propertyHint || ssqSsqHeadingSsqProperty.ssqProperty.hint;
    if (hint === "<p> </p>") {
        hint = undefined;
    }

    const HTMLHint = hint !== undefined && hint !== "" && <HTMLBox value={hint} seeMoreColor="#398BDB" />;

    const title = ssqSsqHeadingSsqProperty.propertyTitle || ssqSsqHeadingSsqProperty.ssqProperty.title;
    const indicator =
        doDisplayIndicator ?
            propertyScoreValue !== null ?
                {
                    value:
                        propertyGradeValue !== null ?
                            `${getDisplayValueForScore(propertyScoreValue, selectedScorePrecision)} / ${getDisplayValueForScore(propertyGradeValue, selectedScorePrecision)}`
                        :   getDisplayValueForScore(propertyScoreValue, selectedScorePrecision),
                    unit: scoreUnit,
                }
            :   undefined
        :   undefined;

    return (
        <WhiteBox
            width="100%"
            padding={{
                xs: theme.spacing(2),
                sm: theme.spacing(2),
                md: theme.spacing(4),
            }}
        >
            <Stack gap={theme.spacing(3)}>
                <Stack
                    width={"100%"}
                    justifyContent={"space-between"}
                    gap={theme.spacing(1)}
                    flexDirection={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "start", sm: "center" }}
                >
                    <Typography
                        variant="big-bold"
                        flex={1}
                        paddingInline={{ xs: theme.spacing(1), sm: theme.spacing(0) }}
                    >
                        {title}
                    </Typography>
                    {indicator && (
                        <FormUnit
                            value={indicator.value}
                            unit={indicator.unit}
                            // color={colorIndicator}
                            BoxProps={{
                                sx: {
                                    top: "-12px",
                                    right: "-12px",
                                },
                            }}
                        />
                    )}
                </Stack>
                <Stack gap={theme.spacing(3)}>
                    {HTMLHint && <Banner variant="themed">{HTMLHint}</Banner>}
                    <FormPropertyComponent
                        accessibleLabel={title}
                        property={property}
                        dimensionValue={dimensionValue}
                        gradeDimensionValue={gradeDimensionValue}
                        position={0}
                        showSubscore={false}
                    />
                </Stack>
            </Stack>
            <CommentDialog />
            <FileUploadAttachmentDialog />
            {/* <FileAttachmentDialog /> */}
            <PropertyActionsPopover />
        </WhiteBox>
    );
};
